import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getConnectedWebsockets, getProductsDetails } from "../utils/api-communication/RestApiCli";
import { resetProductSelection } from "../utils/CommonUtil";
import { getRememberedDeviceUuid, setRememberedDeviceUuid } from "../utils/deviceStorage";
import { resolvePolledScene } from "../utils/headsetRebootPending";
import { productSort } from "../utils/productUtils";
import {
  getDeviceStatus,
  preserveKnownSubstatus,
} from "../components/new_test_ui/new_test_steps/common/DeviceStatus";
import { useSelectedProductSummary } from "./useSelectedProduct";
import { updateDeviceStatus, updateProductBatch, updateScene } from "@actions/productActions";
import { store } from "@store/index";

interface UseDevicePollingOptions {
  enabled?: boolean;
}

interface UseDevicePollingReturn {
  devices: any[];
  selectedDevice: any | null;
  deviceStatus: number;
  loading: boolean;
  handleDeviceChange: (device: any) => void;
  refresh: () => void;
  refreshStatuses: () => void;
}

function applyDeviceSelection(device: any, resetStatus = true) {
  resetProductSelection(
    device.uuid,
    device.version,
    device.puiVersion,
    resetStatus,
    device.scopeName,
    device.volume,
    device.alias,
    device.battery,
  );
}

function getKnownStatus(device: any): number | null {
  if (typeof device?.deviceStatus === "number") return device.deviceStatus;
  if (typeof device?.status === "number") return device.status;
  return null;
}

function getInteractionDate(device: any): Date {
  const value = device?.lastInteraction ?? device?.vrDate;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

function selectDeviceWithKnownStatus(device: any) {
  const knownStatus = getKnownStatus(device);
  const hasKnownStatus = knownStatus !== null && knownStatus >= 0;
  applyDeviceSelection(device, !hasKnownStatus);

  if (hasKnownStatus) {
    store.dispatch(updateDeviceStatus(knownStatus));
    if (typeof device?.scene === "string") {
      store.dispatch(updateScene(device.scene, getInteractionDate(device)));
    }
  }
}

function selectDeviceFromUser(device: any) {
  selectDeviceWithKnownStatus(device);
}

// Overlay the WS server's in-memory connection map (/ws/now) onto the
// registered-device list. Devices absent from the map fall back to
// "NotConnected" (status -1); present ones get live scene/volume/controller
// data. Hoisted so both the full load and the lightweight status refresh share
// one implementation.
function mergeConnectionStatus(baseDevices: any[], connectedList: any[]) {
  return baseDevices.map((device) => {
    const connected = connectedList.find((c) => c.pid === device.uuid);
    if (!connected) {
      return { ...device, scene: "", status: -1, deviceStatus: -1 };
    }
    const isStaleSceneFromPendingReboot =
      Boolean(connected.scene) && resolvePolledScene(device.uuid, connected.scene) === null;
    if (isStaleSceneFromPendingReboot) {
      return { ...device, scene: "", status: -1, deviceStatus: -1 };
    }
    const { lastInteraction, scene, volume, controllers, controllerBattery, isTutorialActive } =
      connected;
    const status = getDeviceStatus(scene, lastInteraction, isTutorialActive === true);
    return {
      ...device,
      lastInteraction,
      vrDate: lastInteraction,
      scene,
      controllers,
      controllerBattery,
      volume,
      status,
      deviceStatus: status,
    };
  });
}

export default function useDevicePolling(
  options: UseDevicePollingOptions = {},
): UseDevicePollingReturn {
  const { enabled = true } = options;
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const selectedProduct = useSelectedProductSummary();
  const selectedProductId = selectedProduct?.uuid ?? null;
  const selectedProductIdRef = useRef(selectedProductId);
  const abortRef = useRef<AbortController | null>(null);
  // Auto-select latches only once a device is actually restored, so a fetch
  // that happens to miss the remembered device doesn't permanently disable
  // auto-restore for the session. Initialized eagerly: if a device was already
  // persisted at mount, treat that as "auto-select already done" so we never
  // override the user's choice on a later fetch.
  const hasAutoSelectedRef = useRef(selectedProductId != null);
  const userPickedRef = useRef(false);

  useEffect(() => {
    selectedProductIdRef.current = selectedProductId;
  }, [selectedProductId]);

  const selectedDevice = useMemo(() => {
    if (!selectedProductId) return null;
    return devices.find((device) => device.uuid === selectedProductId) ?? selectedProduct;
  }, [devices, selectedProduct, selectedProductId]);

  const loadDevices = useCallback(() => {
    if (!enabled) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    function finalizeDevices(merged: any[]) {
      const sortedDevices = [...merged].sort(productSort);
      setDevices(sortedDevices);

      const canAutoSelect =
        !selectedProductIdRef.current &&
        !hasAutoSelectedRef.current &&
        !userPickedRef.current &&
        sortedDevices.length > 0;
      if (canAutoSelect) {
        const rememberedUuid = getRememberedDeviceUuid();
        const remembered = rememberedUuid
          ? sortedDevices.find((d) => d.uuid === rememberedUuid)
          : undefined;
        if (remembered) {
          hasAutoSelectedRef.current = true;
          selectDeviceWithKnownStatus(remembered);
        }
      } else if (selectedProductIdRef.current) {
        // Sync persisted version/alias/scene/status for the already-selected
        // device so a headset firmware update or scene change is reflected
        // without the user having to re-select the device.
        const fresh = sortedDevices.find((d) => d.uuid === selectedProductIdRef.current);
        if (fresh) {
          const persisted = store.getState().product;
          const updates: Record<string, unknown> = {};
          if (fresh.version && fresh.version !== persisted.currentVersion) {
            updates.currentVersion = fresh.version;
          }
          if (fresh.puiVersion && fresh.puiVersion !== persisted.osVersion) {
            updates.osVersion = fresh.puiVersion;
          }
          if (fresh.alias && fresh.alias !== persisted.alias) {
            updates.alias = fresh.alias;
          }
          if (Object.keys(updates).length > 0) {
            store.dispatch(updateProductBatch(updates));
          }
          if (typeof fresh.scene === "string" && fresh.scene !== persisted.scene) {
            store.dispatch(updateScene(fresh.scene, getInteractionDate(fresh)));
          }
          const freshStatus = getKnownStatus(fresh);
          if (freshStatus !== null && freshStatus >= 0) {
            const nextStatus = preserveKnownSubstatus(freshStatus, persisted.deviceStatus);
            if (nextStatus !== persisted.deviceStatus) {
              store.dispatch(updateDeviceStatus(nextStatus));
            }
          }
        }
      }

      setHasFetched(true);
      setLoading(false);
    }

    getProductsDetails(
      (response) => {
        if (controller.signal.aborted) return;
        const baseDevices = response.data || [];

        // Enrich with per-device connection status from the WS server's
        // in-memory client map. Without this, devices fall back to status -1
        // ("NotConnected") in the UI even when the headset is online.
        getConnectedWebsockets(
          (wsResponse) => {
            if (controller.signal.aborted) return;
            const connectedList = wsResponse?.data || [];
            finalizeDevices(mergeConnectionStatus(baseDevices, connectedList));
          },
          () => {
            // /ws/now failed: still surface devices, just without statuses.
            if (controller.signal.aborted) return;
            finalizeDevices(baseDevices);
          },
          null,
        );
      },
      () => {
        if (!controller.signal.aborted) {
          setHasFetched(true);
          setLoading(false);
        }
      },
      null,
      controller.signal,
    );

    return () => {
      controller.abort();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      abortRef.current = null;
      setLoading(false);
      setHasFetched(false);
      return;
    }

    return loadDevices();
  }, [enabled, loadDevices]);

  const handleDeviceChange = useCallback((device: any) => {
    userPickedRef.current = true;
    hasAutoSelectedRef.current = true;
    if (device?.uuid) {
      setRememberedDeviceUuid(device.uuid);
    }
    selectDeviceFromUser(device);
  }, []);

  const refresh = useCallback(() => {
    if (!enabled) return;
    loadDevices();
  }, [enabled, loadDevices]);

  // Lightweight status-only refresh: re-poll /ws/now and overlay live
  // connection state onto the devices already loaded, without re-fetching the
  // (rarely-changing) registered-device list. Keeps every device's status
  // fresh while the selector modal is open — not just the selected one, which
  // useScenePolling already covers on its own.
  const refreshStatuses = useCallback(() => {
    if (!enabled) return;
    getConnectedWebsockets(
      (wsResponse) => {
        const connectedList = wsResponse?.data || [];
        setDevices((prev) =>
          prev.length === 0
            ? prev
            : [...mergeConnectionStatus(prev, connectedList)].sort(productSort),
        );
      },
      () => {
        // /ws/now failed: leave the last-known statuses in place.
      },
      null,
    );
  }, [enabled]);

  // Abort whatever request is in-flight when the component unmounts. The
  // mount-effect cleanup only aborts the controller from its own loadDevices()
  // call — refresh-initiated requests replace abortRef.current and would
  // otherwise resolve onto an unmounted component.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = null;
    },
    [],
  );

  return {
    devices,
    selectedDevice,
    deviceStatus: selectedProduct?.deviceStatus ?? -1,
    loading: loading || (enabled && !hasFetched),
    handleDeviceChange,
    refresh,
    refreshStatuses,
  };
}
